import {
  ref, reactive, computed,
} from '@vue/composition-api'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { formatNumber } from '@core/utils/utils'
import {
  dataToCurrency,
  getDateFromTimestamp,
  filterDataByDate,
} from '@/common-utils'
import { TABS } from '@/constants'
import store from '@/store'
import { GET_SHIPMENT_LIST, GET_SHIPMENT_LIST_ENTITY_NAMES } from '@/store/modules/shipment.module'
import { GET_SHIPMENT_TABLE_COLUMNS } from '@/table-columns-constants'
import { getSearchByEndpoint } from '@/views/apps/order/order-utils'

const { formatCurrency } = utils

const { KP_DATE_FORMAT, SHIPMENT_TEXT } = c

export function useShipmentList() {
  // Reactive state
  const filterDate = ref(null)
  const isLoaded = ref(false)
  const shipments = ref([])
  const totalShipments = ref(0)
  const tableColumns = ref(GET_SHIPMENT_TABLE_COLUMNS())
  const currentTab = ref(TABS.ALL)
  const entityNames = ref([])
  const queryParams = reactive({
    page: 1,
    limit: 10,
  })
  // KP-3336: Vue 2 cannot track property *addition* on a reactive object, so a
  // computed that reads dynamically-added queryParams keys (entityName,
  // min/maxShipmentDate) won't re-run when a filter is first applied. Every
  // filter mutation is immediately followed by loadShipments(), which bumps
  // this tick; the filter computeds depend on it and thus re-evaluate against
  // the current queryParams whenever a filter changes.
  const filtersTick = ref(0)

  // Store-derived computeds
  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)

  // Page computeds
  const hasPagination = computed(() => !!shipments.value.length)
  const isFilterApplied = computed(() => {
    filtersTick.value // eslint-disable-line no-unused-expressions
    return Object.keys(queryParams).length > 2
  })

  // KP-3336: pagination + the active tab (isSelfManaged) are not "filters", so
  // they don't count towards the Clear-filters affordance.
  const FILTER_CLEAR_IGNORED_KEYS = ['page', 'limit', 'isSelfManaged']

  // True when any filter-row selection (retailer/brand or shipment date) is
  // active — drives the "Clear filters" button visibility. Reads filtersTick so
  // it re-evaluates when filters change (Vue 2 can't track the addition of new
  // queryParams keys on its own).
  const hasActiveFilters = computed(() => {
    filtersTick.value // eslint-disable-line no-unused-expressions
    return Object.entries(queryParams).some(
      ([key, value]) => !FILTER_CLEAR_IGNORED_KEYS.includes(key)
        && value !== '' && value !== undefined && value !== null,
    )
  })

  // Reset every filter-row selection while keeping the page size and active tab,
  // then reload from the first page.
  function clearFilters() {
    const { limit, isSelfManaged } = queryParams
    Object.keys(queryParams).forEach(key => delete queryParams[key])
    queryParams.page = 1
    queryParams.limit = limit
    if (isSelfManaged !== undefined) {
      queryParams.isSelfManaged = isSelfManaged
    }
    filterDate.value = null
    loadShipments()
  }

  // Methods
  function currency(data) {
    if (isBrand.value) {
      return data.currencyForBrand
    }
    return data.currency
  }

  function loadEntityNames(searchText = '') {
    const params = {
      search: searchText,
    }
    store.dispatch(GET_SHIPMENT_LIST_ENTITY_NAMES, { role: getSearchByEndpoint(isBrand.value), params }).then(res => {
      entityNames.value = res?.data?.data?.values || []
    })
  }

  function onSelectName(name = '') {
    if (queryParams.entityName === name) {
      queryParams.entityName = ''
    } else {
      queryParams.entityName = name
    }
    loadShipments()
  }

  function onSelectRange(dates) {
    if (dates.length) {
      if (dates.length === 2) {
        queryParams.minShipmentDate = dates[0]
        queryParams.maxShipmentDate = dates[1]
        loadShipments()
      }
    } else {
      delete queryParams.minShipmentDate
      delete queryParams.maxShipmentDate
      loadShipments()
    }
  }

  function onClickTab(tab = '') {
    currentTab.value = tab
    switch (tab) {
      case TABS.ALL: {
        if (queryParams.hasOwnProperty('isSelfManaged')) {
          delete queryParams.isSelfManaged
        }
        break
      }
      case TABS.MARKETPLACE: {
        queryParams.isSelfManaged = false
        break
      }
      case TABS.DIRECT: {
        queryParams.isSelfManaged = true
        break
      }
      default: {
        break
      }
    }
    loadShipments()
  }

  function loadShipments() {
    // KP-3336: synchronously signal the filter computeds that queryParams may
    // have changed (covers keys Vue 2 can't reactively track on addition).
    filtersTick.value += 1
    const params = {
      ...queryParams,
      page: queryParams.page - 1,
    }
    return store.dispatch(GET_SHIPMENT_LIST, params).then(res => {
      shipments.value = res.data.data.shipments
      totalShipments.value = res.data.data.count
      isLoaded.value = true
    }).catch(() => {
      isLoaded.value = true
    })
  }

  function parseStatus(status) {
    if (status === SHIPMENT_TEXT.shipmentDelivered) return 'confirmed'
    return 'pending'
  }

  return {
    // State
    filterDate,
    isLoaded,
    shipments,
    totalShipments,
    tableColumns,
    currentTab,
    entityNames,
    queryParams,

    // Computeds
    isBrand,
    isRetailer,
    hasPagination,
    isFilterApplied,
    hasActiveFilters,
    clearFilters,

    // Methods
    currency,
    loadEntityNames,
    onSelectName,
    onSelectRange,
    onClickTab,
    loadShipments,
    parseStatus,

    // Constants & utilities
    TABS,
    KP_DATE_FORMAT,
    dataToCurrency,
    getDateFromTimestamp,
    filterDataByDate,
    formatNumber,
    formatCurrency,
  }
}
